/**
 * Per-location Risk Calculator configuration.
 *
 * `qualityIndicator` follows the state's own accountability methodology.
 * `geographies` lists the filter levels; each reads a field on the school
 * row (from the risk-calculator endpoint). Client-specific groupings can be
 * added as extra levels with a `valueFor(school)` function, e.g. a partner's
 * service regions keyed by county.
 */
export const RISK_DATASETS = {
  washington: {
    id: 'washington',
    regionName: 'Washington',
    csvPrefix: 'washington',
    // Academic quality uses the state assessment: SBAC ELA and Math, all
    // grades, % meeting standard.
    qualityIndicators: {
      ela: { label: 'SBAC ELA % met standard', shortLabel: 'ELA Proficiency' },
      math: { label: 'SBAC Math % met standard', shortLabel: 'Math Proficiency' },
    },
    qualityMethodology:
      'Academic quality is the share of students meeting standard on the Smarter Balanced (SBAC) ELA and Math assessments, all grades.',
    tourQualityText: 'SBAC ELA and Math % meeting standard',
    geographies: [
      { id: 'state', label: 'Statewide' },
      { id: 'county', label: 'County', field: 'county' },
      { id: 'city', label: 'City', field: 'city' },
      { id: 'schoolDistrict', label: 'School district', field: 'schoolDistrictName' },
    ],
  },
  arkansas: {
    id: 'arkansas',
    regionName: 'Arkansas',
    csvPrefix: 'arkansas',
    // The statewide map scores district-run schools as well as charters.
    includeDistrictSchools: true,
    // Academic quality keeps its 15% default: ATLAS ELA and Math 5% each, plus
    // a binary A/B letter grade at 5%.
    qualityIndicators: {
      ela: { label: 'ATLAS ELA % met standard', shortLabel: 'ELA Proficiency', defaultWeight: 0.05 },
      math: { label: 'ATLAS Math % met standard', shortLabel: 'Math Proficiency', defaultWeight: 0.05 },
    },
    letterGradeIndicator: { defaultWeight: 0.05 },
    qualityMethodology:
      'Academic quality combines the share of students meeting standard on the ATLAS ELA and Math assessments (all grades) with the school’s A–F letter grade: A or B is lower risk, C, D or F is higher risk.',
    tourQualityText: 'ATLAS ELA and Math % meeting standard plus the A/B letter grade',
    geographies: [
      { id: 'state', label: 'Statewide' },
      { id: 'county', label: 'County', field: 'county' },
      { id: 'city', label: 'City', field: 'city' },
      { id: 'schoolDistrict', label: 'School district', field: 'schoolDistrictName' },
    ],
  },
};

export function getRiskDataset(locationId) {
  return RISK_DATASETS[locationId] || null;
}

export function geographyValue(school, level) {
  if (!level || level.id === 'state') return null;
  if (typeof level.valueFor === 'function') return level.valueFor(school);
  return school?.[level.field] ?? null;
}
